import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';

import DialogRoundedModal from '../../../../../components/DialogRoundedModal';
import { SaveButton, CancelButton } from '../../../../../components/Button/ActionButtons';
import { DisabledTag } from '../../../../../components/Tags';
import GenericDataGrid from '../../../../components/GenericDataGrid';
import QuickSearchToolbar from '../../../../components/QuickSearchToolbar';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import UserContext from '../../../../../context/UserContext';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useLogger from '../../../../../hooks/useLogger';
import useQuickSearch from '../../../../../hooks/useQuickSearch';
import useSessionStore from '../../../../../hooks/useSessionStore';
import useToast from '../../../../hooks/useToast';
import WorkoutPicks, { WORKOUT_PICKS_CAP } from '../../../../../Model/WorkoutPicks';
import Workout from '../../../../../Model/Workout';
import { CoachingActivity } from '../../../../../utils/log';
import { formatSeconds } from '../../../../../utils/date';
import { isWorkoutPublished } from '../../../../../utils/longSession';

import {
  ModalContent,
  DataGridContainer,
  NameCell,
  HelperText,
} from './styles';
import texts from './texts.json';

const PAGE_SIZE = 20;

const ManageCoachPicksModal = ({ onClose, workouts, workoutsIncludeArchived }) => {
  const { userId: coachId } = useContext(UserContext);
  const { userId: editorId } = useSessionStore();
  const { showToast } = useToast();
  const { logCoachingActivity } = useLogger();
  const isMountedRef = useComponentMounted();

  const [picksDoc, setPicksDoc] = useState(null);
  const [pickedWorkouts, setPickedWorkouts] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [isBusy, setIsBusy] = useState(true);

  const activeWorkouts = useMemo(() => (
    workoutsIncludeArchived ? workouts.filter((w) => !w.isArchived) : workouts
  ), [workouts, workoutsIncludeArchived]);

  useEffect(() => {
    const loadPicks = async () => {
      try {
        const loadedPicksDoc = await WorkoutPicks.getByCoachId(coachId);
        const picked = loadedPicksDoc.workoutIds.length
          ? await Workout.getWorkoutsByIds(loadedPicksDoc.workoutIds)
          : [];
        if (!isMountedRef.current) return;
        setPicksDoc(loadedPicksDoc);
        setPickedWorkouts(picked);
        setSelectedIds(picked.map((w) => w.id));
      } catch (error) {
        if (!isMountedRef.current) return;
        showToast(texts.loadError, { error: true });
        onClose();
      } finally {
        if (isMountedRef.current) setIsBusy(false);
      }
    };
    loadPicks();
  }, [coachId, onClose, showToast, isMountedRef]);

  const orderedWorkouts = useMemo(() => {
    const pickedIdSet = new Set(pickedWorkouts.map((w) => w.id));
    const unpickedWorkouts = activeWorkouts.filter((workout) => !pickedIdSet.has(workout.id));
    return [...pickedWorkouts, ...unpickedWorkouts];
  }, [activeWorkouts, pickedWorkouts]);

  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: texts.columns.name,
      quickSearch: true,
      flex: 30,
      renderCell: ({ row }) => (
        <NameCell>
          <span>{row.name}</span>
          {row.isArchived && <DisabledTag>{texts.archivedTag}</DisabledTag>}
        </NameCell>
      ),
    },
    {
      field: 'duration',
      headerName: texts.columns.duration,
      flex: 10,
      valueGetter: ({ row }) => (row.estimatedDuration
        ? formatSeconds(row.estimatedDuration)
        : texts.emptyCell),
    },
    {
      field: 'id',
      headerName: texts.columns.id,
      quickSearch: true,
      flex: 20,
    },
  ], []);

  const { filteredRows, toolbarProps } = useQuickSearch(orderedWorkouts, columns);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const capReached = selectedIds.length >= WORKOUT_PICKS_CAP;

  /*
    A workout that is not READY (a long session still processing, or one that failed) is never shown
    to clients, so picking it would only take a slot. An existing pick stays selectable so it can be
    removed.
  */
  const isRowSelectable = useCallback(({ row }) => (
    selectedIdSet.has(row.id) || (!capReached && isWorkoutPublished(row))
  ), [selectedIdSet, capReached]);

  const handleSelectionChange = useCallback((newSelection) => {
    if (newSelection.length > WORKOUT_PICKS_CAP) {
      showToast(format(texts.capExceeded, { cap: WORKOUT_PICKS_CAP }), { error: true });
    }
    setSelectedIds(newSelection.slice(0, WORKOUT_PICKS_CAP));
  }, [showToast]);

  const counterLabel = format(texts.counter, {
    selected: selectedIds.length,
    cap: WORKOUT_PICKS_CAP,
  });

  let helperLabel = counterLabel;
  if (capReached) helperLabel = `${counterLabel} — ${texts.capReachedHint}`;
  else if (selectedIds.length === 0) helperLabel = `${counterLabel} — ${texts.emptyHint}`;

  const handleSave = async () => {
    if (!picksDoc) return;
    setIsBusy(true);
    try {
      await picksDoc.setWorkoutIds(selectedIds, editorId);
      logCoachingActivity(CoachingActivity.UPDATED_COACH_PICKS, {
        workoutIds: selectedIds,
      });
      showToast(texts.saveSuccess);
      onClose();
    } catch (error) {
      showToast(texts.saveError, { error: true });
      if (isMountedRef.current) setIsBusy(false);
    }
  };

  return (
    <DialogRoundedModal
      open
      onClose={onClose}
      title={texts.title}
      description={format(texts.subtitle, { cap: WORKOUT_PICKS_CAP })}
      fullWidth
      maxWidth="md"
      actionButtons={(
        <>
          <CancelButton onClick={onClose}>{texts.cancel}</CancelButton>
          <SaveButton
            onClick={handleSave}
          >
            {texts.save}
          </SaveButton>
        </>
      )}
    >
      <ModalContent>
        <HelperText $warning={capReached}>{helperLabel}</HelperText>
        <DataGridContainer>
          <GenericDataGrid
            rows={filteredRows}
            columns={columns}
            checkboxSelection
            disableRowSelectionOnClick
            keepNonExistentRowsSelected
            isRowSelectable={isRowSelectable}
            rowSelectionModel={selectedIds}
            onRowSelectionModelChange={handleSelectionChange}
            pagination
            initialState={{
              pagination: { paginationModel: { pageSize: PAGE_SIZE } },
            }}
            pageSizeOptions={[PAGE_SIZE]}
            slots={{ toolbar: QuickSearchToolbar }}
            slotProps={{
              toolbar: {
                ...toolbarProps,
                autoFocus: true,
                placeholder: texts.searchPlaceholder,
              },
            }}
          />
        </DataGridContainer>
      </ModalContent>
      <LoadingOverlay isLoading={isBusy} />
    </DialogRoundedModal>
  );
};

ManageCoachPicksModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  workouts: PropTypes.arrayOf(PropTypes.instanceOf(Workout)).isRequired,
  workoutsIncludeArchived: PropTypes.bool,
};

ManageCoachPicksModal.defaultProps = {
  workoutsIncludeArchived: false,
};

export default ManageCoachPicksModal;
